import { peekFitnessGuidance } from '@/i18n/translations';
import { getExplicitFitnessMovement } from '@/lib/fitnessExerciseIdentity';
import { detectLangFromTags, normalizeLang } from '@/i18n/locale';

/** Original static instruction for ten exact Cardio contracts.
 * Authored sources and apparatus adaptations:
 * docs/research/fitness-rea-20261015/guidance-drafts/cardio10/sources.json.
 * No personalized assessment, camera observation or professional review is implied.
 */
export const CARDIO_EXERCISE_GUIDANCE_IDS = [
  "walking",
  "running",
  "burpee",
  "jump-rope",
  "battle-ropes",
  "treadmill",
  "stationary-bike",
  "elliptical",
  "stair-climber",
  "rowing-machine"
] as const;
export type CardioExerciseGuidanceId = typeof CARDIO_EXERCISE_GUIDANCE_IDS[number];
type GuidanceCopy = readonly [string, string, string, string, string];
export type CardioExerciseGuidanceDictionary = Record<CardioExerciseGuidanceId, GuidanceCopy>;
const supported = new Set<string>(CARDIO_EXERCISE_GUIDANCE_IDS);
function isCardioGuidanceId(value: string): value is CardioExerciseGuidanceId {
  return supported.has(value);
}

/** Exact app identity/canonical name; provider IDs and display labels are not inferred. */
export function getCardioExerciseGuidance(exerciseName: string, lang?: string): { steps: string[]; cues: string[] } | null {
  const id = getExplicitFitnessMovement(exerciseName)?.id;
  if (!id || !isCardioGuidanceId(id)) return null;
  const language = normalizeLang(lang) ?? detectLangFromTags(lang ? [lang] : []) ?? 'EN';
  const copy = peekFitnessGuidance(language, 'cardio', id);
  if (!copy) return null;
  return { steps: copy.slice(0, 3), cues: copy.slice(3) };
}
